import { Badge, Button } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Printer, PrinterCreate } from '@warehouse/shared';
import { Plus, Printer as PrinterIcon, RefreshCw, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createPrinter,
  deletePrinter,
  listPrinters,
  pollPrinterStatus,
  testPrint,
  updatePrinter,
} from '../api';
import {
  PrinterFormModal,
  emptyPrinterForm,
  printerToForm,
  type PrinterFormValues,
} from '../components/PrinterFormModal';
import { PrinterStateBadge } from '../components/StatusBadges';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useConfirm } from '../../../components/ConfirmDialog';
import { useListState } from '../../../hooks/useListState';
import { formatRelative } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * The printer registry (admin.settings.printers to write, warehouse.print to
 * read). Rows are database rows — the old system kept this list as one JSON
 * blob in a Redis pod with an emptyDir volume, so every printer the customer
 * configured vanished on restart (docs/recon/printing-scanners.md A6 #9).
 */
export function PrintersPage(): ReactNode {
  const { t } = useTranslation(['devicesPrinting', 'common']);
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { state, setState } = useListState({ defaultSort: 'name' });
  const [editing, setEditing] = useState<{ id: string | null; values: PrinterFormValues } | null>(null);

  const query = useQuery({
    queryKey: ['printers', listQueryKey(state)],
    queryFn: () => listPrinters(state),
  });
  useLiveQuery(['printers', 'print-jobs'], ['printers']);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['printers'] });
  };

  const save = useMutation({
    mutationFn: (input: { id: string | null; values: PrinterCreate }) =>
      input.id ? updatePrinter(input.id, input.values) : createPrinter(input.values),
    onSuccess: (_result, input) => {
      toastSuccess(
        input.id
          ? t('devicesPrinting:printers.toast.saved')
          : t('devicesPrinting:printers.toast.created'),
      );
      setEditing(null);
      invalidate();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePrinter(id),
    onSuccess: () => {
      toastSuccess(t('devicesPrinting:printers.toast.deleted'));
      invalidate();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const test = useMutation({
    mutationFn: (printer: Printer) => testPrint(printer.id),
    onSuccess: (_result, printer) => {
      toastSuccess(t('devicesPrinting:printers.toast.testPrinted', { name: printer.name }));
      void queryClient.invalidateQueries({ queryKey: ['print-jobs'] });
      invalidate();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const poll = useMutation({
    mutationFn: (printer: Printer) => pollPrinterStatus(printer.id),
    onSuccess: () => {
      toastSuccess(t('devicesPrinting:printers.toast.polled'));
      invalidate();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const columns: DataTableColumn<Printer>[] = [
    {
      id: 'name',
      header: t('devicesPrinting:printers.columns.name'),
      sortField: 'name',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{row.name}</span>
          {row.isDefault ? (
            <Badge tone="brand" size="sm">
              {t('devicesPrinting:printers.columns.isDefault')}
            </Badge>
          ) : null}
          {row.isActive ? null : <Badge size="sm">{t('devicesPrinting:status.inactive')}</Badge>}
        </div>
      ),
    },
    {
      id: 'host',
      header: t('devicesPrinting:printers.columns.host'),
      cell: (row) => <span className="font-mono text-xs">{`${row.host}:${row.port}`}</span>,
    },
    {
      id: 'kind',
      header: t('devicesPrinting:printers.columns.kind'),
      sortField: 'kind',
      hideBelow: 'md',
      cell: (row) => t(`devicesPrinting:printers.kind.${row.kind}`),
    },
    {
      id: 'station',
      header: t('devicesPrinting:printers.columns.station'),
      sortField: 'station',
      hideBelow: 'lg',
      cell: (row) => row.station ?? '—',
    },
    {
      id: 'stockCode',
      header: t('devicesPrinting:printers.columns.stockCode'),
      hideBelow: 'xl',
      cell: (row) => row.stockCode ?? '—',
    },
    {
      id: 'status',
      header: t('devicesPrinting:printers.columns.status'),
      cell: (row) => <PrinterStateBadge status={row.lastStatus} />,
    },
    {
      id: 'queue',
      header: t('devicesPrinting:printers.columns.queue'),
      align: 'right',
      cell: (row) => (
        <span className={row.failedJobs > 0 ? 'text-amber-600' : undefined}>
          {t('devicesPrinting:printers.queueSummary', {
            queued: row.queuedJobs,
            failed: row.failedJobs,
          })}
        </span>
      ),
    },
    {
      id: 'lastSeen',
      header: t('devicesPrinting:printers.columns.lastSeen'),
      sortField: 'lastSeenAt',
      hideBelow: 'lg',
      cell: (row) => (row.lastSeenAt ? formatRelative(row.lastSeenAt) : '—'),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon={<PrinterIcon />}
            loading={test.isPending && test.variables?.id === row.id}
            onClick={() => {
              test.mutate(row);
            }}
          >
            {t('devicesPrinting:printers.actions.testPrint')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw />}
            loading={poll.isPending && poll.variables?.id === row.id}
            onClick={() => {
              poll.mutate(row);
            }}
          >
            {t('devicesPrinting:printers.actions.pollStatus')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditing({ id: row.id, values: printerToForm(row) });
            }}
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 />}
            aria-label={t('devicesPrinting:printers.actions.delete')}
            onClick={() => {
              void (async () => {
                const ok = await confirm({
                  title: t('devicesPrinting:printers.confirm.deleteTitle'),
                  message: t('devicesPrinting:printers.confirm.deleteMessage'),
                  confirmLabel: t('common:actions.delete'),
                });
                if (ok) remove.mutate(row.id);
              })();
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {dialog}
      <PageHeader
        title={t('devicesPrinting:printers.title')}
        subtitle={t('devicesPrinting:printers.subtitle')}
        actions={
          <Button
            icon={<Plus />}
            onClick={() => {
              setEditing({ id: null, values: emptyPrinterForm() });
            }}
          >
            {t('devicesPrinting:printers.new')}
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('devicesPrinting:printers.searchPlaceholder')}
        emptyTitle={t('devicesPrinting:printers.empty')}
        emptyDescription={t('devicesPrinting:printers.emptyDescription')}
      />

      {editing ? (
        <PrinterFormModal
          initial={editing.values}
          saving={save.isPending}
          onClose={() => {
            setEditing(null);
          }}
          onSubmit={(values) => {
            save.mutate({ id: editing.id, values });
          }}
        />
      ) : null}
    </>
  );
}
